import { useCallback, useEffect } from 'react';

import { isUndefined } from 'lodash-es';

import useEffectOnce from 'hooks/use-effect-once';
import { useLocalStorageState } from 'hooks/use-local-storage-state';
import { LaunchDarklyFlag, useFlag, useLDContext } from 'state/launchdarkly';
import { StorageKeys } from 'utils/local-storage';

import { createInRestaurantRedemptionCartEntry } from './create-in-restaurant-redemption-cart-entry';
import {
  addOrReplaceIfExist,
  addOrUpdateQuantityIfExist,
  addToInRestaurantCart,
  excludeEntryType,
  findCartElementByIncentive,
  getFirstOccurrenceInCart,
  removeCartEntry,
  updateCartEntryQuantity,
} from './in-restaurant-cart';
import {
  IFindEntryOptions,
  IInRestaurantRedemptionCart,
  IInRestaurantRedemptionCartEntry,
  IInRestaurantRedemptionEntryType,
  IUseInRestaurantRedemptionCart,
  InRestaurantIncentives,
  InRestaurantRedemptionObject,
  isRedemptionObjectLegacyOffer,
  isRedemptionObjectStackableOffer,
  isRewardCartEntry,
} from './types';
import { isQuantityValid } from './utils';

/**
 * useInRestaurantRedemptionCart holds the cart state and expose an API to interact with.
 */
export const useInRestaurantRedemptionCart = (): IUseInRestaurantRedemptionCart => {
  const { ldUserIsAuthenticated } = useLDContext();
  const enableOffers3_0 = useFlag(LaunchDarklyFlag.ENABLE_OFFERS_3_0);
  const [
    inRestaurantRedemptionCart,
    updateCart,
    resetInRestaurantRedemptionCart,
  ] = useLocalStorageState<IInRestaurantRedemptionCart>({
    key: StorageKeys.IN_RESTAURANT_REDEMPTION,
    defaultReturnValue: [],
  });

  // retro compatibility
  useEffectOnce(() => {
    if (inRestaurantRedemptionCart?.length) {
      let shouldUpdate = false;
      const newState = inRestaurantRedemptionCart.reduce<IInRestaurantRedemptionCart>(
        (acc, entry) => {
          if (
            entry.type === IInRestaurantRedemptionEntryType.OFFER &&
            isRedemptionObjectLegacyOffer(entry.details)
          ) {
            entry.type = IInRestaurantRedemptionEntryType.LEGACY_OFFER;
            shouldUpdate = true;
          }

          acc.push(entry);

          return acc;
        },
        []
      );

      if (shouldUpdate) {
        updateCart(newState);
      }
    }
  });

  const existEntryTypeInRestaurantRedemptionCart = useCallback(
    (type: IInRestaurantRedemptionEntryType): boolean =>
      !isUndefined(getFirstOccurrenceInCart(inRestaurantRedemptionCart, { type })),
    [inRestaurantRedemptionCart]
  );

  const findInRestaurantCartEntryByIncentive = (incentive: InRestaurantIncentives) =>
    findCartElementByIncentive(inRestaurantRedemptionCart, incentive);

  // Cart Entry CRUD
  const addInRestaurantRedemptionEntry = (redemptionObject: InRestaurantRedemptionObject) => {
    const cartEntry = createInRestaurantRedemptionCartEntry(redemptionObject);

    // an invalid cart entry
    if (!cartEntry) {
      return false;
    }

    let addingMethod;
    let options: IFindEntryOptions | undefined = undefined;

    if (isRewardCartEntry(cartEntry)) {
      addingMethod = addOrUpdateQuantityIfExist;
    } else if (isRedemptionObjectStackableOffer(cartEntry.details)) {
      addingMethod = addToInRestaurantCart;
    } else {
      options = {
        keyPath: ['details', 'offer', 'isStackable'],
        value: false,
        type: cartEntry.type,
      };
      addingMethod = addOrReplaceIfExist;
    }

    // Updates the quantity for rewards that already exist into the cart,
    // that's not considered an `adding` and `isActionSuccess` will be `false`
    const { isActionSuccess, cartResult } = addingMethod(
      inRestaurantRedemptionCart,
      cartEntry,
      options
    );

    if (isActionSuccess) {
      updateCart(cartResult);
    }

    return isActionSuccess;
  };

  const removeInRestaurantRedemptionEntry = useCallback(
    (cartEntry: IInRestaurantRedemptionCartEntry) => {
      updateCart(prevCart => removeCartEntry(prevCart, cartEntry));
    },
    [updateCart]
  );

  const removeTypeFromCart = useCallback(
    (typeToRemove: IInRestaurantRedemptionEntryType) => {
      updateCart(prevCart => excludeEntryType(prevCart, typeToRemove));
    },
    [updateCart]
  );

  const clearInRestaurantRedemptionAllRewards = useCallback(() => {
    removeTypeFromCart(IInRestaurantRedemptionEntryType.REWARD);
  }, [removeTypeFromCart]);

  const updateInRestaurantRedemptionEntryQuantity = useCallback(
    (cartEntry: IInRestaurantRedemptionCartEntry, quantity: number) => {
      if (isQuantityValid(quantity) && isRewardCartEntry(cartEntry)) {
        updateCart(
          prevCart => updateCartEntryQuantity(prevCart, { cartEntry, quantity }).cartResult
        );
      }
    },
    [updateCart]
  );

  /**
   * The hook propose is legacy compatibility, the only dependencies are the flag
   * `enableOffers3_0` and `ldUserIsAuthenticated` because avoiding memoizing functions
   * the only received updates should be related to the flags.
   */
  useEffect(() => {
    // LD targeting issues, here we are assuming that the feature is available only for logged in users
    if (!ldUserIsAuthenticated) {
      return;
    }

    const offerTypeToRemove = enableOffers3_0
      ? IInRestaurantRedemptionEntryType.LEGACY_OFFER
      : IInRestaurantRedemptionEntryType.OFFER;

    if (existEntryTypeInRestaurantRedemptionCart(offerTypeToRemove)) {
      removeTypeFromCart(offerTypeToRemove);
    }
  }, [
    enableOffers3_0,
    existEntryTypeInRestaurantRedemptionCart,
    ldUserIsAuthenticated,
    removeTypeFromCart,
  ]);

  return {
    inRestaurantRedemptionCart,
    addInRestaurantRedemptionEntry,
    removeInRestaurantRedemptionEntry,
    updateInRestaurantRedemptionEntryQuantity,
    findInRestaurantCartEntryByIncentive,
    resetInRestaurantRedemptionCart,
    existEntryTypeInRestaurantRedemptionCart,
    clearInRestaurantRedemptionAllRewards,
    isInRestaurantRedemptionCartEmpty: !inRestaurantRedemptionCart?.length,
  };
};
